import z from "zod";
import { LabelFormatIdScheme, PrinterIdScheme } from "./ids";
import { PrinterType } from "./enums/PrinterType";
import { ReceiptTextSize } from "./enums/ReceiptTextSize";
import { PaperWidth } from "./enums/PaperWidth";
import { IpPrinterFlavor } from "./enums/IpPrinterFlavor";
import { PrinterCut } from "./enums/PrinterCut";

const PrinterBaseScheme = z.object({
  id: PrinterIdScheme,
  name: z.string(),
  type: z.enum(PrinterType),
  cut: z.enum(PrinterCut).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const IpPrinterDBScheme = PrinterBaseScheme.extend({
  type: z.literal(PrinterType.IP_PRINTER),
  flavor: z.enum(IpPrinterFlavor),
  paperWidth: z.enum(PaperWidth),
  textSize: z.enum(ReceiptTextSize),
  ip: z.string(),
});

export const BuiltInSunmiPrinterDBScheme = PrinterBaseScheme.extend({
  type: z.literal(PrinterType.BUILT_IN_SUNMI),
  paperWidth: z.enum(PaperWidth),
  textSize: z.enum(ReceiptTextSize),
});

export const ZebraIpPrinterDBScheme = PrinterBaseScheme.extend({
  type: z.literal(PrinterType.ZEBRA_IP_PRINTER),
  ip: z.string(),
  labelFormatId: LabelFormatIdScheme.nullable(),
});

export const SunmiCloudPrinterDBScheme = PrinterBaseScheme.extend({
  type: z.literal(PrinterType.SUNMI_CLOUD),
  serialNumber: z.string(),
});

export const PrinterDBScheme = z.discriminatedUnion("type", [
  IpPrinterDBScheme,
  BuiltInSunmiPrinterDBScheme,
  ZebraIpPrinterDBScheme,
  SunmiCloudPrinterDBScheme,
]);

export const RestGetPrinter = PrinterDBScheme;

export const RestCreatePrinter = z.discriminatedUnion("type", [
  IpPrinterDBScheme.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  }),
  BuiltInSunmiPrinterDBScheme.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  }),
  ZebraIpPrinterDBScheme.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  }),
  SunmiCloudPrinterDBScheme.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  }),
]);

export const RestUpdatePrinter = z.discriminatedUnion("type", [
  IpPrinterDBScheme.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
    .partial()
    .extend({
      type: z.literal(PrinterType.IP_PRINTER),
    }),
  BuiltInSunmiPrinterDBScheme.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
    .partial()
    .extend({
      type: z.literal(PrinterType.BUILT_IN_SUNMI),
    }),
  ZebraIpPrinterDBScheme.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
    .partial()
    .extend({
      type: z.literal(PrinterType.ZEBRA_IP_PRINTER),
    }),
  SunmiCloudPrinterDBScheme.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
    .partial()
    .extend({
      type: z.literal(PrinterType.SUNMI_CLOUD),
    }),
]);

export type IpPrinter = z.infer<typeof IpPrinterDBScheme>;
export type BuiltInSunmiPrinter = z.infer<typeof BuiltInSunmiPrinterDBScheme>;
export type ZebraIpPrinter = z.infer<typeof ZebraIpPrinterDBScheme>;
export type SunmiCloudPrinter = z.infer<typeof SunmiCloudPrinterDBScheme>;
export type DBPrinter = z.infer<typeof PrinterDBScheme>;
export type RestPrinter = z.infer<typeof RestGetPrinter>;
export type RestCreatePrinter = z.infer<typeof RestCreatePrinter>;
export type RestUpdatePrinter = z.infer<typeof RestUpdatePrinter>;
